'use client';

import { Component, ErrorInfo, ReactNode } from 'react';
import classnames from 'classnames';
import Box from '../../atoms/Box/Box';
import Typography from '../../atoms/Typography/Typography';
import Button from '../../atoms/Button/Button';
import styles from './ErrorBoundary.module.scss';

type Theme = 'dark' | 'light';

export interface ErrorBoundaryProps {
  /** Child components to render */
  children: ReactNode;
  /** Theme for the error UI */
  theme?: Theme;
  /** Custom fallback UI to display when an error occurs */
  fallback?: ReactNode | ((error: Error, errorInfo: ErrorInfo) => ReactNode);
  /** Callback function called when an error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Whether to show technical error details (useful for development) */
  showDetails?: boolean;
  /** Custom reset button text */
  resetButtonText?: string;
  /** Callback when reset button is clicked */
  onReset?: () => void;
  /** Custom class name */
  className?: string;
  /** Data test ID for testing */
  dataTestId?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * ErrorBoundary component that catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of crashing the component tree.
 *
 * Based on React's Error Boundary pattern: https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary
 *
 * @example
 * ```tsx
 * <ErrorBoundary theme="dark" onError={(error) => logToService(error)}>
 *   <MyComponent />
 * </ErrorBoundary>
 * ```
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error details for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Update state with error info
    this.setState({
      errorInfo,
    });

    // Call optional error callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReset = (): void => {
    // Reset the error boundary state
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });

    // Call optional reset callback
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  render(): ReactNode {
    const {
      children,
      theme = 'dark',
      fallback,
      showDetails = false,
      resetButtonText = 'Try Again',
      className,
      dataTestId = 'error-boundary',
    } = this.props;

    if (this.state.hasError && this.state.error) {
      // If custom fallback is provided, use it
      if (fallback) {
        if (typeof fallback === 'function') {
          return fallback(this.state.error, this.state.errorInfo!);
        }
        return fallback;
      }

      // Default fallback UI with Lucid theme
      return (
        <Box className={classnames(styles.errorBoundary, className)} data-testid={dataTestId}>
          <Box mb={24}>
            <Typography variant="headline" size="large" color="error-1" theme={theme}>
              Something went wrong
            </Typography>
          </Box>

          <Box mb={32}>
            <Typography variant="body" size="medium" color="gray-5" theme={theme}>
              We apologize for the inconvenience. An unexpected error has occurred.
            </Typography>
          </Box>

          {showDetails && this.state.error && (
            <Box className={styles.errorDetailsContainer}>
              <Box
                className={classnames(styles.errorDetails, {
                  [styles.dark]: theme === 'dark',
                  [styles.light]: theme === 'light',
                })}
              >
                <Typography variant="label" size="small" color="error-1" theme={theme}>
                  Error Details:
                </Typography>
                <Box mt={8}>
                  <pre className={styles.errorPre}>
                    <Typography variant="body" size="small" color="gray-5" theme={theme}>
                      {this.state.error.toString()}
                    </Typography>
                  </pre>
                </Box>
                {this.state.errorInfo?.componentStack && (
                  <Box mt={12}>
                    <Typography variant="label" size="small" color="gray-5" theme={theme}>
                      Component Stack:
                    </Typography>
                    <Box mt={8}>
                      <pre className={styles.componentStackPre}>
                        <Typography variant="body" size="small" color="gray-6" theme={theme}>
                          {this.state.errorInfo.componentStack}
                        </Typography>
                      </pre>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          <Button
            type="primary"
            size="medium"
            theme={theme}
            label={resetButtonText}
            handleClick={this.handleReset}
            dataTestId={`${dataTestId}-reset-button`}
            gaComponent="ErrorBoundary"
          />
        </Box>
      );
    }

    // No error, render children normally
    return children;
  }
}

export default ErrorBoundary;
